import { createContext, useContext, useEffect, useState } from 'react';
import { login as apiLogin, register as apiRegister, getCurrentUser, logout as apiLogout, googleAuth } from '../services/api';
import { useSocket } from './SocketContext';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { disconnect: disconnectSocket } = useSocket();

    // Check if user is logged in on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            getCurrentUser()
                .then(response => {
                    setCurrentUser(response.data);
                })
                .catch(() => {
                    localStorage.removeItem('token');
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, []);

    // Register function
    const signup = async (name, email, password, phone) => {
        try {
            const response = await apiRegister({ name, email, password, phone });
            if (response.user) {
                setCurrentUser(response.user);
                // Optionally remove this if OTP is completely removed, but leaving it is fine
                localStorage.setItem('pendingVerificationEmail', email);
            }
            return { data: response }; // Match expected structure if not already mapped correctly
        } catch (error) {
            throw error.response?.data || error;
        }
    };

    // Login function
    const login = async (email, password) => {
        try {
            const response = await apiLogin(email, password);
            setCurrentUser(response.user);
            
            // Store user role for socket connection
            if (response.user) {
                localStorage.setItem('userRole', response.user.role);
                localStorage.setItem('userId', response.user.id);
            }
            
            return response;
        } catch (error) {
            throw error.response?.data || error;
        }
    };

    // Logout function
    const logout = async () => {
        // Disconnect socket for regular users
        if (currentUser && currentUser.role === 'user') {
            disconnectSocket();
        }

        await apiLogout();
        setCurrentUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('pendingVerificationEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        sessionStorage.removeItem('tempLoginPassword');
    };

    // Google Sign-In function
    const loginWithGoogle = async (credential) => {
        try {
            const response = await googleAuth(credential);
            setCurrentUser(response.user);
            if (response.user) {
                localStorage.setItem('userRole', response.user.role);
                localStorage.setItem('userId', response.user.id);
            }
            return response;
        } catch (error) {
            throw error;
        }
    };

    const value = {
        currentUser,
        isAuthenticated: !!currentUser,
        setCurrentUser,
        signup,
        login,
        logout,
        loginWithGoogle,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};